/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Bot, ShieldCheck, Activity, Play, Info, CheckCircle2,
  AlertOctagon, Trophy,
} from 'lucide-react';
import {
  getAgenticStatus, enableAgentic, disableAgentic,
  runMonitorNow, listAgenticAlerts,
} from '@/lib/agenticAi';
import AgenticAlertCard from '@/components/agentic/AgenticAlertCard';

const CONSENT_TEXT = `Autorizzo SanitaNexus ad analizzare automaticamente i miei dati di salute (misurazioni IoT e triage) ogni 30 minuti per rilevare anomalie cliniche e propormi azioni preventive. Comprendo che:
- L'analisi è eseguita da un algoritmo deterministico + AI di supporto (non sostituisce il medico).
- I miei dati non vengono condivisi con terzi senza mio ulteriore consenso.
- Posso disattivare il servizio in qualsiasi momento.
- In caso di emergenza reale devo chiamare il 112.`;

const simpleHash = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0;
  }
  return String(h);
};

const AgenticHealthPage = () => {
  const [status, setStatus] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        getAgenticStatus(),
        listAgenticAlerts(),
      ]);
      setStatus(s);
      setAlerts(a.items || []);
    } catch (e) {
       
      console.error('[Agentic] load failed', e);
      toast.error('Impossibile caricare l\'Assistente AI');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const enabled = status?.settings?.enabled;

  const toggle = async (next) => {
    setBusy(true);
    try {
      if (next) {
        await enableAgentic({
          consent_text_hash: simpleHash(CONSENT_TEXT),
          contact_channels: ['in_app'],
        });
        toast.success('Assistente AI attivato');
      } else {
        await disableAgentic();
        toast.success('Assistente AI disattivato');
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    } finally {
      setBusy(false);
    }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const summary = await runMonitorNow();
      const created = summary.alerts_created?.length || 0;
      if (created > 0) {
        toast.success(`Analisi completata: ${created} nuovi alert`);
      } else {
        toast.success('Analisi completata: nessuna anomalia rilevata');
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    } finally {
      setBusy(false);
    }
  };

  const activeAlerts = alerts.filter((a) => a.status === 'new' || a.status === 'acknowledged');
  const resolvedAlerts = alerts.filter((a) => a.status !== 'new' && a.status !== 'acknowledged');

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 rounded-2xl" />
        <Skeleton className="h-32 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="agentic-health-page">
      <div>
        <h1 className="font-display font-bold text-2xl md:text-3xl flex items-center gap-2">
          <Bot className="h-7 w-7 text-primary" />
          Assistente AI Proattivo
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Monitora i tuoi dati di salute 24/7 e ti avvisa in caso di anomalie.
        </p>
      </div>

      {/* Master toggle card */}
      <Card className="relative overflow-hidden" data-testid="agentic-master-card">
        <div className={enabled ? 'absolute inset-x-0 top-0 h-1 bg-emerald-500' : 'absolute inset-x-0 top-0 h-1 bg-muted'} />
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <CardTitle className="font-display flex items-center gap-2">
                {enabled ? (
                  <>
                    <CheckCircle2 className="h-5 w-5 text-emerald-500" /> Attivo
                  </>
                ) : (
                  <>
                    <Info className="h-5 w-5 text-muted-foreground" /> Disattivato
                  </>
                )}
              </CardTitle>
              <CardDescription>
                {enabled
                  ? 'L\'assistente analizza i tuoi dati ogni 30 minuti e crea alert quando rileva anomalie cliniche.'
                  : 'Attiva l\'assistente per ricevere alert automatici sulle tue misurazioni.'}
              </CardDescription>
            </div>
            <Switch
              checked={!!enabled}
              onCheckedChange={toggle}
              disabled={busy}
              data-testid="agentic-toggle"
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {!enabled && (
            <div className="rounded-xl bg-primary/5 border border-primary/20 p-3 text-xs">
              <div className="font-semibold mb-1 flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-primary" /> Consenso al monitoraggio
              </div>
              <p className="text-muted-foreground whitespace-pre-line leading-relaxed">
                {CONSENT_TEXT}
              </p>
            </div>
          )}
          {enabled && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-xl border border-border p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Alert attivi</div>
                <div className="font-display font-bold text-2xl mt-1">{activeAlerts.length}</div>
              </div>
              <div className="rounded-xl border border-border p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Ultima analisi</div>
                <div className="font-medium text-sm mt-1">
                  {status?.last_run
                    ? new Date(status.last_run.ran_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })
                    : 'Mai'}
                </div>
              </div>
              <div className="rounded-xl border border-border p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Frequenza</div>
                <div className="font-medium text-sm mt-1">Ogni 30 min</div>
              </div>
              <div className="rounded-xl border border-border p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Consenso</div>
                <div className="font-medium text-sm mt-1">
                  {status?.settings?.consent_given_at
                    ? new Date(status.settings.consent_given_at).toLocaleDateString('it-IT')
                    : '—'}
                </div>
              </div>
            </div>
          )}
          {enabled && (
            <div className="flex items-center gap-2 pt-1">
              <Button
                size="sm"
                variant="outline"
                onClick={runNow}
                disabled={busy}
                data-testid="agentic-run-now"
              >
                <Play className="h-3.5 w-3.5 mr-1" /> Analizza ora
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Active alerts */}
      {enabled && activeAlerts.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-2">
            <AlertOctagon className="h-4 w-4 text-red-500" />
            <h2 className="font-display font-semibold text-lg">Da gestire</h2>
            <Badge variant="secondary">{activeAlerts.length}</Badge>
          </div>
          <div className="space-y-3">
            <AnimatePresence>
              {activeAlerts.map((a) => (
                <AgenticAlertCard key={a.id} alert={a} onChanged={load} />
              ))}
            </AnimatePresence>
          </div>
        </section>
      )}

      {enabled && activeAlerts.length === 0 && (
        <Alert className="border-emerald-500/40 bg-emerald-500/5">
          <Trophy className="h-4 w-4 text-emerald-500" />
          <AlertTitle>Tutto sotto controllo</AlertTitle>
          <AlertDescription>
            Nessuna anomalia rilevata sui tuoi ultimi dati. Continua a misurare regolarmente.
          </AlertDescription>
        </Alert>
      )}

      {/* Resolved history */}
      {resolvedAlerts.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-2">
            <Activity className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-display font-semibold text-lg">Storico</h2>
            <Badge variant="outline">{resolvedAlerts.length}</Badge>
          </div>
          <div className="space-y-3">
            {resolvedAlerts.slice(0, 20).map((a) => (
              <AgenticAlertCard key={a.id} alert={a} onChanged={load} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default AgenticHealthPage;
